import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import AppNavbar from "../../../../components/AppNavbar/AppNavbar";
import { TrackPageView } from "../../../../config/mixpanelConfig";

import "./settings.css";

// Panels — created in parallel per the build contract. Each is a default
// export taking props { profile, role }.
import ProfilePanel from "./panels/ProfilePanel";
import OrganizationPanel from "./panels/OrganizationPanel";
import BillingPlanPanel from "./panels/BillingPlanPanel";
import UsersPanel from "./panels/UsersPanel";
import CommandCenterPanel from "./panels/CommandCenterPanel";
import NotificationsPanel from "./panels/NotificationsPanel";
import IntegrationsPanel from "./panels/IntegrationsPanel";
import SecurityPanel from "./panels/SecurityPanel";
import WhiteLabelPanel from "./panels/WhiteLabelPanel";

// Top-tier ("Gold") plan names. The live top plan is internally "Agency"
// (displayed as "Gold"); the newer catalogue calls it "Scale". White label is
// gated to this tier.
const TOP_TIER_PLANS = ["agency", "gold", "scale"];

/**
 * Settings sub-nav config — ordered exactly as the prototype's
 * `.settings-sidenav` (data-tab / data-roles). Each item carries its
 * feather-style 18x18 icon, label, allowed roles, and the panel component
 * it mounts.
 */
const NAV_ITEMS = [
  {
    tab: "profile",
    label: "Profile",
    roles: ["admin", "staff"],
    Component: ProfilePanel,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    ),
  },
  {
    tab: "organization",
    label: "Organization",
    roles: ["admin"],
    Component: OrganizationPanel,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="2" width="16" height="20" rx="2" />
        <path d="M9 22v-4h6v4" />
        <path d="M8 6h.01M16 6h.01M8 10h.01M16 10h.01M8 14h.01M16 14h.01" />
      </svg>
    ),
  },
  {
    tab: "billing-plan",
    label: "Billing & plan",
    roles: ["admin"],
    Component: BillingPlanPanel,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="5" width="20" height="14" rx="2" />
        <line x1="2" y1="10" x2="22" y2="10" />
      </svg>
    ),
  },
  {
    tab: "users",
    label: "Users & roles",
    roles: ["admin"],
    Component: UsersPanel,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
  {
    tab: "command-center",
    label: "Command Center",
    roles: ["admin"],
    Component: CommandCenterPanel,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <polyline points="9 12 11 14 15 10" />
      </svg>
    ),
  },
  {
    tab: "notifications",
    label: "Notifications",
    roles: ["admin", "staff"],
    Component: NotificationsPanel,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
    ),
  },
  {
    tab: "integrations",
    label: "Apps",
    roles: ["admin"],
    Component: IntegrationsPanel,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7" rx="1.5" />
        <rect x="14" y="3" width="7" height="7" rx="1.5" />
        <rect x="14" y="14" width="7" height="7" rx="1.5" />
        <rect x="3" y="14" width="7" height="7" rx="1.5" />
      </svg>
    ),
  },
  {
    tab: "white-label",
    label: "Design",
    roles: ["admin"],
    requiresTopPlan: true,
    Component: WhiteLabelPanel,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2 9.5 8.5 3 11l6.5 2.5L12 20l2.5-6.5L21 11l-6.5-2.5z" />
      </svg>
    ),
  },
  {
    tab: "security",
    label: "Security",
    roles: ["admin", "staff"],
    Component: SecurityPanel,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
    ),
  },
];

const SettingsShell = () => {
  const { search } = useLocation();
  const { profile } = useSelector((state) => state?.profile || {});

  // Role discriminator: the existing Settings page gates on
  // `profile.user.type === "staff"` (admin = anything else). Match that and
  // map it onto the design's data-roles ("admin" | "staff").
  const role = profile?.user?.type === "staff" ? "staff" : "admin";

  // White label is unlocked only for an ACTIVE subscription on the top-tier
  // ("Gold") plan — a lapsed/cancelled Gold subscription does not qualify.
  const subscription = profile?.subscription;
  const planName = (subscription?.packageId?.name || "").toLowerCase();
  const subscribedToGold = subscription?.isActive === true && TOP_TIER_PLANS.includes(planName);
  const isTopTier = subscribedToGold;

  // Nav items are visible when the role matches and (if gated) the plan tier
  // allows it.
  const visibleItems = useMemo(
    () =>
      NAV_ITEMS.filter(
        (item) => item.roles.includes(role) && (!item.requiresTopPlan || isTopTier)
      ),
    [role, isTopTier]
  );

  // Honor a simple ?tab= query param (falls back to "profile"). A `planToken`
  // param (e.g. from an "update your plan" email link) deep-links straight to
  // Billing & plan, where BillingPlanPanel auto-opens the update-plan modal.
  const params = new URLSearchParams(search);
  const requestedTab = params.get("tab") || (params.get("planToken") ? "billing-plan" : null);
  const [activeTab, setActiveTab] = useState("profile");

  useEffect(() => {
    TrackPageView("Setting");
  }, []);

  // If the requested/active tab is not available to this role, fall back to
  // "profile" (mirrors the prototype's nav fallback).
  useEffect(() => {
    const allowed = visibleItems.map((i) => i.tab);
    if (requestedTab && allowed.includes(requestedTab)) {
      setActiveTab(requestedTab);
    } else if (!allowed.includes(activeTab)) {
      setActiveTab("profile");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedTab, role]);

  return (
    <div className="settings-redesign">
      <AppNavbar title={"Settings"} profile={profile} />

      <div className="settings-layout">
        {/* ────────── Settings sub-nav ────────── */}
        <aside className="settings-sidenav">
          {visibleItems.map((item) => (
            <div
              key={item.tab}
              className={`settings-nav-item${activeTab === item.tab ? " active" : ""}`}
              data-tab={item.tab}
              data-roles={item.roles.join(" ")}
              onClick={() => setActiveTab(item.tab)}
            >
              {item.icon}
              {item.label}
            </div>
          ))}
        </aside>

        {/* ────────── Active panel ────────── */}
        <div className="settings-content">
          {visibleItems.map((item) => {
            const Panel = item.Component;
            return (
              <div
                key={item.tab}
                className={`settings-panel${activeTab === item.tab ? " active" : ""}`}
                data-panel={item.tab}
              >
                {activeTab === item.tab && (
                  <Panel profile={profile} role={role} />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SettingsShell;
